import { Controller } from "@hotwired/stimulus"
import {
  lockBodyScroll,
  unlockBodyScroll,
} from "~/controllers/mixins/useBodyScrollLock"
import { useMainMenuOverlay } from "~/controllers/mixins/useMainMenuOverlay"

// Scroll speed in pixels per millisecond. Pushing the page away has to be
// deliberate before the heading gives up its place, while the smallest pull
// back up brings it straight back — the asymmetry is what makes the heading
// feel eager rather than sticky.
const hideVelocity = 0.5
const showVelocity = 0.1
// Each sample is folded into the running speed rather than replacing it, so a
// single stuttering frame cannot flip the heading on its own.
const velocitySmoothing = 0.3
// A gap this long means a new gesture, not a continuation of the last one.
const gestureGap = 150
// The heading always stands its ground within reach of the top of the page.
const restingOffset = 64

export default class extends Controller {
  static values = {
    open: Boolean,
    overlay: Boolean,
    headingHidden: Boolean,
  }

  connect() {
    const mql = window.matchMedia("(width > 870px)")
    mql.addEventListener("change", this.handleMediaQueryChange.bind(this))

    // The toggle button is the menu's lowest painted row, and unlike the
    // heading it never moves, so it makes a stable line to watch.
    this.stopObservingOverlays = useMainMenuOverlay(
      this,
      this.element.querySelector("button"),
    )

    this.lastScrollY = Math.max(window.scrollY, 0)
    this.lastSampledAt = performance.now()
    this.velocity = 0
    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
    this.updateHeadingHidden()
  }

  disconnect() {
    this.stopObservingOverlays?.()
    window.removeEventListener("scroll", this.handleScroll)
    cancelAnimationFrame(this.scrollFrame)
  }

  handleScroll() {
    cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = requestAnimationFrame(() => this.updateHeadingHidden())
  }

  // Driven by how fast the page is moving rather than how far it has come: a
  // decisive scroll down clears the heading away, and any scroll back up
  // returns it, wherever on the page that happens. Between the two thresholds
  // the heading holds whatever state it is in, so drifting through a page
  // never makes it flicker.
  updateHeadingHidden() {
    const y = Math.max(window.scrollY, 0)
    this.trackVelocity(y)

    if (y <= restingOffset) {
      this.headingHiddenValue = false
    } else if (this.velocity > hideVelocity) {
      this.headingHiddenValue = true
    } else if (this.velocity < -showVelocity) {
      this.headingHiddenValue = false
    }
  }

  trackVelocity(y) {
    const now = performance.now()
    const elapsed = now - this.lastSampledAt

    if (elapsed > 0) {
      const sample = (y - this.lastScrollY) / elapsed
      this.velocity =
        elapsed > gestureGap
          ? sample
          : this.velocity + (sample - this.velocity) * velocitySmoothing
    }

    this.lastScrollY = y
    this.lastSampledAt = now
  }

  toggle() {
    this.openValue = !this.openValue
  }

  close() {
    this.openValue = false
  }

  openValueChanged() {
    if (this.openValue) {
      lockBodyScroll()
    } else {
      unlockBodyScroll()
    }
  }

  handleMediaQueryChange(e) {
    if (e.matches) {
      this.openValue = false
    }
  }
}
